import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, useLoad } from "@/lib/api";
import { useMe } from "@/lib/me";
import { ROLE_LABEL, fmtDate } from "@/lib/format";
import { PageHead } from "@/components/Bits";

const RoleSelect = ({ value, roles, onChange, testid, disabled }) => (
  <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="h-9 rounded-md border border-input bg-white px-2 text-sm disabled:opacity-60">
    {[...new Set([value, ...roles])].map((r) => <option key={r} value={r} disabled={!roles.includes(r)}>{ROLE_LABEL[r] || r}</option>)}
  </select>
);

export default function TeamPage() {
  const api = useApi();
  const me = useMe();
  const t = useLoad("/users");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const roles = t.data?.roles || [];
  const canEdit = (r) => me.role === "owner" || ["staff", "pending"].includes(r);

  const invite = async (e) => {
    e.preventDefault();
    try {
      const r = await api("post", "/invites", { email, role });
      toast.success(r.applied ? `${email} is now ${ROLE_LABEL[role]}` : `${email} will get ${ROLE_LABEL[role]} access when they sign up`);
      setEmail(""); t.reload();
    } catch (err) { toast.error(err.message); }
  };
  const change = async (uid, r) => {
    try { await api("patch", `/users/${uid}/role`, { role: r }); toast.success("Role updated"); } catch (err) { toast.error(err.message); }
    t.reload();
  };

  return (
    <div>
      <PageHead title="Team" sub={me.role === "owner" ? "Owners can manage everyone." : "Managers can add and approve staff."} />
      <form onSubmit={invite} className="mb-8 flex flex-col gap-2 rounded-3xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center rise" data-testid="invite-form">
        <UserPlus className="hidden h-5 w-5 text-slate-400 sm:block" />
        <Input type="email" required placeholder="colleague@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1" data-testid="invite-email-input" />
        <RoleSelect value={role} roles={roles.filter((r) => r !== "pending")} onChange={setRole} testid="invite-role-select" />
        <Button type="submit" data-testid="invite-submit-button">Give access</Button>
      </form>
      <p className="-mt-6 mb-8 text-xs text-slate-500">They sign up at this app with that email and create their own password. Anyone who signs up without an invite waits for approval below.</p>

      {t.data?.invites?.length > 0 && (
        <div className="mb-6">
          <h2 className="mb-2 text-lg font-bold">Invited, not signed up yet</h2>
          <div className="flex flex-wrap gap-2" data-testid="invites-list">
            {t.data.invites.map((i) => (
              <span key={i.email} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-3 pr-1 text-sm">
                {i.email} <span className="text-xs text-slate-500">{ROLE_LABEL[i.role]}</span>
                <button onClick={() => api("delete", `/invites/${encodeURIComponent(i.email)}`).then(t.reload)} className="rounded-full p-1 hover:bg-slate-100" data-testid={`invite-remove-${i.email}`}><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="team-list">
        {(t.data?.users || []).map((u) => (
          <div key={u.user_id} className={`flex flex-col gap-2 border-b border-slate-100 px-4 py-3 last:border-0 sm:flex-row sm:items-center ${u.role === "pending" ? "bg-amber-50/50" : ""}`} data-testid={`team-row-${u.email}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{u.name || u.email}{u.user_id === t.data.me && <span className="ml-2 text-xs text-slate-400">(you)</span>}</p>
              <p className="truncate text-xs text-slate-500">{u.email} · joined {fmtDate(u.joined_at)}</p>
            </div>
            <RoleSelect value={u.role} roles={roles} disabled={!canEdit(u.role)} onChange={(r) => change(u.user_id, r)} testid={`role-select-${u.email}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
